"use client";

import React from "react";
import { motion, type Variants } from "framer-motion";
import { ServerCrash, AlertTriangle, Terminal, RotateCcw } from "lucide-react";

// Note: app/error.tsx receives 'error' and 'reset' props
export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // ─── MAINTENANCE REDIRECT ───────────────────────────────────────────
  React.useEffect(() => {
    let cancelled = false;
    const confirmMaintenance = async () => {
      try {
        const response = await fetch(`/api/maintenance/status?t=${Date.now()}`, { cache: "no-store" });
        if (!response.ok || cancelled) return;
        const status = await response.json();
        if (status?.isActive === true && !window.location.pathname.startsWith("/maintenance")) {
          const returnTo = `${window.location.pathname}${window.location.search}`;
          window.location.replace(
            `/maintenance?reason=MAINTENANCE_MODE&returnTo=${encodeURIComponent(returnTo)}`,
          );
        }
      } catch {
        // Keep the actual error UI. A network exception does not prove maintenance.
      }
    };
    void confirmMaintenance();
    return () => { cancelled = true; };
  }, [error]);
  
  // Shared Standard Variants
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100, damping: 10 } },
  };

  // Custom "Shaking" Animation for crash
  const shakeVariants: Variants = {
    hidden: { x: 0 },
    visible: {
      x: [-5, 5, -5, 5, 0],
      transition: { duration: 0.5, delay: 1, repeat: Infinity, repeatDelay: 3 },
    },
  };

  return (
    <div className="min-h-screen w-full bg-neutral-50 dark:bg-neutral-950 flex flex-col items-center justify-center p-4 relative overflow-hidden font-sans text-neutral-900 dark:text-neutral-100 selection:bg-red-100 selection:text-red-900">
      {/* Red-tinted Background Background */}
       <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05] bg-[size:24px_24px] bg-[linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)]" />
       <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-500/10 via-transparent to-transparent dark:from-red-900/20" />

      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="relative z-10 max-w-lg w-full">
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-2xl shadow-red-500/5 p-8 md:p-12 text-center overflow-hidden relative">
          {/* Red top glow */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50" />

          {/* Icon Section */}
          <motion.div variants={itemVariants} className="flex justify-center mb-8">
            <div className="relative group">
              <div className="absolute inset-0 bg-red-500/20 dark:bg-red-400/20 blur-2xl rounded-full transition-all duration-500 group-hover:bg-orange-500/20" />
              <motion.div className="relative bg-white dark:bg-neutral-800 p-5 rounded-full border border-neutral-100 dark:border-neutral-700 shadow-sm flex items-center justify-center">
                <motion.div variants={shakeVariants} className="relative z-10">
                  <AlertTriangle className="w-10 h-10 text-neutral-400 dark:text-neutral-600 absolute -left-2 -top-2" />
                  <ServerCrash className="w-10 h-10 text-red-600 dark:text-red-500 relative z-20" />
                </motion.div>
                {/* Orbiting red particle */}
                <motion.div animate={{ rotate: -360 }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }} className="absolute inset-0 rounded-full">
                  <div className="w-2 h-2 bg-red-500 rounded-full absolute -top-1 left-1/2 -translate-x-1/2 shadow-[0_0_10px_rgba(239,68,68,0.8)]" />
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.h1 variants={itemVariants} className="text-3xl font-extrabold tracking-tight mb-3 bg-clip-text text-transparent bg-gradient-to-r from-neutral-900 via-red-800 to-neutral-900 dark:from-white dark:via-red-400 dark:to-white">
            500: Critical Failure
          </motion.h1>
          <motion.p variants={itemVariants} className="text-neutral-500 dark:text-neutral-400 mb-8 leading-relaxed font-medium">
            An unexpected exception occurred. System core dumped. Our engineers have been notified.
          </motion.p>

          {/* Terminal Badge */}
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-neutral-950 dark:bg-black border border-neutral-800 dark:border-neutral-800 text-xs font-mono text-red-400 mb-8 justify-center shadow-inner">
            <Terminal className="w-3 h-3" />
            <span>{`> ERROR_CODE: KERNEL_PANIC`}</span>
             {/* Blinking cursor */}
            <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.8, repeat: Infinity }} className="w-1.5 h-3 bg-red-400 inline-block ml-1" />
          </motion.div>

          {/* Reset/Retry Button (Specific to error.tsx) */}
          <motion.div variants={itemVariants}>
            <button onClick={reset} className="group relative inline-flex w-full md:w-auto items-center justify-center gap-2 px-6 py-3 bg-neutral-900 dark:bg-white text-white dark:text-black rounded-lg font-medium text-sm overflow-hidden transition-all hover:bg-neutral-800 dark:hover:bg-neutral-200 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)] dark:hover:shadow-[0_0_20px_rgba(239,68,68,0.15)]">
              <RotateCcw className="w-4 h-4 transition-transform group-hover:-rotate-45" />
              <span>Reboot System (Retry)</span>
               {/* Button Sheen */}
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 dark:via-black/10 to-transparent transition-transform group-hover:translate-x-full duration-700 ease-in-out" />
            </button>
          </motion.div>
        </div>
        
        {/* Footer */}
        <motion.div variants={itemVariants} className="mt-8 text-center">
           <p className="text-xs text-neutral-400 dark:text-neutral-500 font-mono">
            [ SYSTEM_ID: {new Date().getFullYear()}.CRASH_{Math.floor(Math.random() * 999)} ]
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
